import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import {
  CharacterIcon, PurposeIcon, CourageIcon, LoveIcon, HopeIcon, OnwardIcon,
} from '../../img/icons/about';
import { journeyImages } from '../../img/journey';
import { useHeaderFooter } from '../../hooks';
import { AppDataContext } from '../../context';
import { getLocalizedImage } from '../../utils';

import messages from './messages';

import overviewImg from '../../img/about/ABOUTUS-OVERVIEW-LWC.jpg';

interface AboutUsProps {
  children?: React.ReactNode
}
const AboutUs: React.FC<AboutUsProps> = ({ children }) => {
  const intl = useIntl();
  const { headerContent, footerContent } = useHeaderFooter(children);
  const { allowedCourses } = useContext(AppDataContext);

  const firstOfAllowedCourseId = allowedCourses[0]?.course_id;
  const journeySrc = getLocalizedImage(journeyImages);

  return (
    <>
      <input type="hidden" id="anPageName" name="page" defaultValue="about" />
      <div className="container-center-horizontal">
        <div className="about screen">
          <div className="homepage">
            {headerContent}
            <div className="overlap-group6">
              <div className="hero-image">
                <div className="overlap-group-1">
                  <img
                    className="overlap-group-item"
                    src={overviewImg}
                    alt=""
                  />
                  <h1 className="about-us recklessneue-regular-normal-white-100-1px">
                    <span className="recklessneue-regular-normal-white-100-1px" style={{ fontStyle: 'italic' }}>
                      {intl.formatMessage(messages.course)}{' '}
                    </span>
                    <span className="instrumentserif-normal-white-100-1px">
                      {intl.formatMessage(messages.overview)}
                    </span>
                  </h1>
                </div>
              </div>
            </div>
          </div>
          <div className="course-overview-text">
            <div className="overlap-group5">
              <div className="built-to-flourish recklessneue-book-normal-congress-blue-48px">
                {intl.formatMessage(messages.lookWithinLeadBeyondTitle)}
              </div>
              <p className="lorem-ipsum-dolor-si-1 neuehaasgroteskdisplaypro-45-light-congress-blue-24px">
                {intl.formatMessage(messages.lookWithinLeadBeyondText1)}
              </p>
              <p className="lorem-ipsum-dolor-si-1 neuehaasgroteskdisplaypro-45-light-congress-blue-24px">
                {intl.formatMessage(messages.lookWithinLeadBeyondText2)}
              </p>
            </div>
          </div>
          <div className="legatum-container the-course">
            <div className="c-section">
              <div className="leg-title">{intl.formatMessage(messages.theCourseTitle)}</div>
              <p className="leg-p">
                {intl.formatMessage(messages.theCourseText1)}
              </p>
              <p className="leg-p">
                {intl.formatMessage(messages.theCourseText2)}
              </p>
            </div>
            <div className="wayfinder-img-container">
              <img
                alt={intl.formatMessage(messages.journeyImageAlt)}
                className="w-100"
                src={journeySrc}
              />
            </div>
            <div className="c-section">
              <div className="leg-title">{intl.formatMessage(messages.whyNeededTitle)}</div>
              <p className="leg-p">
                {intl.formatMessage(messages.whyNeededText1)}
              </p>
              <p className="leg-p">
                {intl.formatMessage(messages.whyNeededText2)}
              </p>
              <p className="leg-p">
                {intl.formatMessage(messages.whyNeededText3)}
              </p>
            </div>
            <div className="c-section">
              <div className="leg-title">{intl.formatMessage(messages.whoIsThisForTitle)}</div>
              <p className="leg-p">
                {intl.formatMessage(messages.whoIsThisForText1)}
              </p>
              <p className="leg-p">
                {intl.formatMessage(messages.whoIsThisForText2)}
              </p>
              <p className="leg-p">
                {intl.formatMessage(messages.whoIsThisForText3)}
              </p>
            </div>
            <div className="items-center space-bottom">
              <div className="leg-primary-button">
                <NavLink to={`/course-overview/${firstOfAllowedCourseId}`}>
                  {intl.formatMessage(messages.enrollButton)}
                </NavLink>
              </div>
            </div>
            <div className="c-section">
              <div className="leg-title">{intl.formatMessage(messages.courseOverviewTitle)}</div>
              <div className="overview-box space-top">
                <div className="icon">
                  <CharacterIcon />
                </div>
                <div className="overview-text">
                  <div className="ov-title">
                    {intl.formatMessage(messages.characterTitle)}
                  </div>
                  <div className="ov-list">
                    <ul>
                      <li>{intl.formatMessage(messages.characterListItem1)}</li>
                      <li>{intl.formatMessage(messages.characterListItem2)}</li>
                      <li>{intl.formatMessage(messages.characterListItem3)}</li>
                      <li>{intl.formatMessage(messages.characterListItem4)}</li>
                      <li>{intl.formatMessage(messages.characterListItem5)}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="overview-box space-top">
                <div className="icon">
                  <PurposeIcon />
                </div>
                <div className="overview-text">
                  <div className="ov-title">
                    {intl.formatMessage(messages.purposeTitle)}
                  </div>
                  <div className="ov-list">
                    <ul>
                      <li>{intl.formatMessage(messages.purposeListItem1)}</li>
                      <li>{intl.formatMessage(messages.purposeListItem2)}</li>
                      <li>{intl.formatMessage(messages.purposeListItem3)}</li>
                      <li>{intl.formatMessage(messages.purposeListItem4)}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="overview-box space-top">
                <div className="icon">
                  <CourageIcon />
                </div>
                <div className="overview-text">
                  <div className="ov-title">
                    {intl.formatMessage(messages.courageTitle)}
                  </div>
                  <div className="ov-list">
                    <ul>
                      <li>{intl.formatMessage(messages.courageListItem1)}</li>
                      <li>{intl.formatMessage(messages.courageListItem2)}</li>
                      <li>{intl.formatMessage(messages.courageListItem3)}</li>
                      <li>{intl.formatMessage(messages.courageListItem4)}</li>
                      <li>{intl.formatMessage(messages.courageListItem5)}</li>
                      <li>{intl.formatMessage(messages.courageListItem6)}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="overview-box space-top">
                <div className="icon">
                  <LoveIcon />
                </div>
                <div className="overview-text">
                  <div className="ov-title">
                    {intl.formatMessage(messages.loveTitle)}
                  </div>
                  <div className="ov-list">
                    <ul>
                      <li>{intl.formatMessage(messages.loveListItem1)}</li>
                      <li>{intl.formatMessage(messages.loveListItem2)}</li>
                      <li>{intl.formatMessage(messages.loveListItem3)}</li>
                      <li>{intl.formatMessage(messages.loveListItem4)}</li>
                      <li>{intl.formatMessage(messages.loveListItem5)}</li>
                      <li>{intl.formatMessage(messages.loveListItem6)}</li>
                      <li>{intl.formatMessage(messages.loveListItem7)}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="overview-box space-top">
                <div className="icon">
                  <HopeIcon />
                </div>
                <div className="overview-text">
                  <div className="ov-title">
                    {intl.formatMessage(messages.hopeTitle)}
                  </div>
                  <div className="ov-list">
                    <ul>
                      <li>{intl.formatMessage(messages.hopeListItem1)}</li>
                      <li>{intl.formatMessage(messages.hopeListItem2)}</li>
                      <li>{intl.formatMessage(messages.hopeListItem3)}</li>
                      <li>{intl.formatMessage(messages.hopeListItem4)}</li>
                      <li>{intl.formatMessage(messages.hopeListItem5)}</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="overview-box space-top">
                <div className="icon">
                  <OnwardIcon />
                </div>
                <div className="overview-text">
                  <div className="ov-title">
                    {intl.formatMessage(messages.onwardTitle)}
                  </div>
                  <div className="ov-list">
                    <ul>
                      <li>{intl.formatMessage(messages.onwardListItem1)}</li>
                      <li>{intl.formatMessage(messages.onwardListItem2)}</li>
                      <li>{intl.formatMessage(messages.onwardListItem3)}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default AboutUs;
